import { Set } from 'immutable';
import { ID } from '@repo/utils/types';
import { HighlightEvtType } from '..';

interface ToggleHandlerCtx {
  selectedItems: Set<ID>;
  setSelectedItems: (items: Set<ID>) => void;
  lastToggledCheckboxIdx: number;
  setLastToggledCheckboxIdx: (idx: number) => void;
  setHighlightedIdx?: ([idx, evtType, shiftKeyDown]: [number, HighlightEvtType, boolean]) => void;
}

// select/deselect a single item in a list
export function toggleRowSelection(ctx: ToggleHandlerCtx, item: any, idx: number) {
  const { selectedItems, setSelectedItems, setLastToggledCheckboxIdx } = ctx;

  if (selectedItems.has(item.id)) {
    setSelectedItems(selectedItems.delete(item.id));
  } else {
    setSelectedItems(selectedItems.add(item.id));
  }

  setLastToggledCheckboxIdx(idx);
}

export function resetSelectionIfNotInBatch(ctx: ToggleHandlerCtx, item: any, idx: number) {
  if (!ctx.selectedItems.has(item.id)) {
    ctx.setSelectedItems(Set([item.id]));
    ctx.setLastToggledCheckboxIdx(idx);
    ctx.setHighlightedIdx([idx, 'mouse', false]);
  }
}

// select/deselect a group of items in a list
export function toggleGroupSelection(
  ctx: ToggleHandlerCtx,
  items: Array<any>,
  item: any,
  idx: number,
) {
  const { selectedItems, setSelectedItems, setLastToggledCheckboxIdx, lastToggledCheckboxIdx } =
    ctx;

  if (lastToggledCheckboxIdx == -1 || idx == lastToggledCheckboxIdx) {
    toggleRowSelection(ctx, item, idx);
  }

  const targetSelected = selectedItems.has(item.id);
  const startIdx = Math.min(idx, lastToggledCheckboxIdx);
  const endIdx = Math.max(idx, lastToggledCheckboxIdx);
  const selectionIDs = items.slice(startIdx, endIdx + 1).map(({ id }) => id);

  if (targetSelected) {
    setSelectedItems(selectedItems.subtract(selectionIDs));
  } else {
    setSelectedItems(selectedItems.union(selectionIDs));
  }

  setLastToggledCheckboxIdx(idx);
}

// During a shift+arrow selection/deselection event (while the shift key is
// still pressed), we need to track certain state to have the correct selection/
// deselection events given another arrow key press, up or down.
interface ShiftArrowSelectState {
  anchorIdx: number;
  minIdx: number;
  maxIdx: number;
  focusIdx: number;
  shouldUpdateSelection: boolean;
}

// Move basis of shift+arrow list item selection to the currently focused idx
export const resetShiftArrowSelectState = (idx: number) => ({
  anchorIdx: idx,
  minIdx: idx,
  maxIdx: idx,
  focusIdx: idx,
  shouldUpdateSelection: false,
});

// Handle updating ShiftArrowSelectState based on last state
export const shiftArrowSelectReducer = (
  state: ShiftArrowSelectState,
  [type, focusIdx]: [string, number?],
): ShiftArrowSelectState => {
  switch (type) {
    case 'reset':
      return resetShiftArrowSelectState(focusIdx);

    case 'update':
      let minIdx = focusIdx < state.minIdx ? focusIdx : state.minIdx;
      let maxIdx = focusIdx > state.maxIdx ? focusIdx : state.maxIdx;
      return {
        ...state,
        minIdx,
        maxIdx,
        focusIdx,
        shouldUpdateSelection: true,
      };

    default:
      return state;
  }
};
